import React, { Fragment, forwardRef } from 'react';
import Type from 'prop-types';
import { useEffectOnce } from 'react-use';
import { AutoSizer, List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { Divider, List as MuiList, ListSubheader } from "@mui/material";
import { groupByDates, getRelativeDateTime } from 'utils/date';
import PaymentItem from '../PaymentItem';
import styles from './PaymentList.module.css';

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 200,
});

const PaymentList = forwardRef((props, ref) => {
  const { operations: operationsProp, listHeight } = props;
  const operationDates = groupByDates(operationsProp, 'operationDate');
  const rowCount = operationDates.length;

  useEffectOnce(() => {
    cache.clearAll();
  });

  const rowRenderer = (rowData) => {
    const { index, parent, key, style } = rowData;
    const [operationDate, operations] = operationDates[index];
    const { date: formattedOperationDate } = getRelativeDateTime(operationDate);

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
        <div style={style}>
          <MuiList
            classes={{ root: styles.list }}
            subheader={<ListSubheader component="div">{formattedOperationDate}</ListSubheader>}
            disablePadding
          >
            {operations.map((operation, idx) => {
              return (
                <Fragment key={operation.id}>
                  <PaymentItem operation={operation} />

                  {operations.length - 1 !== idx && <Divider />}
                </Fragment>
              );
            })}
          </MuiList>
        </div>
      </CellMeasurer>
    );
  };

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <List
          ref={ref}
          height={listHeight}
          overscanRowCount={2}
          rowCount={rowCount}
          deferredMeasurementCache={cache}
          rowHeight={cache.rowHeight}
          rowRenderer={rowRenderer}
          width={width}
        />
      )}
    </AutoSizer>
  );
});

PaymentList.propTypes = {
  listHeight: Type.number.isRequired,
  operations: Type.arrayOf(Type.shape(PaymentItem.propTypes.operation)).isRequired,
};

export default PaymentList;
